import React from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import withRouterActions from 'routes/utils/withRouterActions'
import { selectors as NotesSelectors } from 'data/notes'
import { selectors as UISelectors } from 'data/ui'
import { selectors as ConfigSelectors } from 'data/config'
import { setActiveNoteId } from 'data/publicUI/actions'
import ShareAPI from 'remote/share'
import RoutesHelper from 'helpers/RoutesHelper'
import { getNoteShareLink } from 'helpers/URLHelper'
import { dataFetched } from 'data/graphql'

function mapStateToProps(state, { noteId }) {
  const note = NotesSelectors.getNote(state, noteId)
  const channelId = note ? note.channelId : undefined

  return {
    apiEndpoint: ConfigSelectors.getApiEndpoint(state),
    channelId,
    fromNoteId: UISelectors.getActiveNoteId(state),
  }
}

function getRedirectURL({
  isNotePublic,
  apiEndpoint,
  notePublicShareToken,
  noteTitle,
  noteId,
}) {
  return isNotePublic
    ? RoutesHelper.publicShareNoteIndex({
        token: notePublicShareToken,
        title: noteTitle,
      })
    : getNoteShareLink(
        { apiEndpoint },
        {
          noteId,
          noteTitle,
          notePublicShareToken,
        }
      )
}

export default ({ NoteLinkPlugin }) => ({
  decorator,
  publicChannelId,
} = {}) => {
  const noteLinkDecorator = DecoratedComponent =>
    function NoteLinkDecorated({
      routerPush,
      apiEndpoint,
      setActiveNoteId,
      dataFetched,
      ...props
    }) {
      const { noteId, channelId } = props
      const [isNotePublic, setIsNotePublic] = React.useState(false)
      const [noteTitle, setNoteTitle] = React.useState()
      const [noteChannelId, setNoteChannelId] = React.useState()
      const [notePublicShareToken, setNotePublicShareToken] = React.useState()

      const url = getRedirectURL({
        apiEndpoint,
        isNotePublic,
        noteTitle,
        noteChannelId,
        notePublicShareToken,
      })

      function onClick() {
        const isChannelLink =
          Boolean(noteChannelId) && noteChannelId === publicChannelId
        if (isChannelLink) {
          return setActiveNoteId(noteId)
        }
        routerPush(url)
      }

      React.useEffect(
        () => {
          async function fetchNotePublicInfo() {
            let result
            try {
              result = await ShareAPI.getNotePublicToken(
                { apiEndpoint },
                { noteId }
              )
            } catch (error) {
              // Errors when note is private
              setIsNotePublic(false)
              return
            }
            const isChannelLink = result.channelId === publicChannelId

            setNoteTitle(result.title)
            setNoteChannelId(result.channelId)
            setNotePublicShareToken(result.publicShareToken)
            dataFetched({
              id: noteId,
              __typename: 'PublicNote',
              ...result,
            })

            setIsNotePublic(!!result.publiclySharedAt || isChannelLink)
          }

          fetchNotePublicInfo()
        },
        [apiEndpoint, channelId, noteId, dataFetched]
      )

      return (
        <DecoratedComponent
          {...props}
          url={url}
          isNotePublic={isNotePublic}
          onClick={onClick}
        />
      )
    }

  const decorators = [
    ...(decorator ? [decorator] : []),
    withRouterActions,
    connect(
      mapStateToProps,
      { setActiveNoteId, dataFetched }
    ),
    noteLinkDecorator,
  ]

  return NoteLinkPlugin({
    isPublic: true,
    decorator: compose(...decorators),
  })
}
